import { useState, useEffect } from 'react';
import { X, Package, Truck, FolderOpen, Clock, MessageSquare } from 'lucide-react';
import PoLineLink from '../common/PoLineLink';
import PoHeaderLink from '../common/PoHeaderLink';
import { toast } from 'sonner';
import { Card } from '../ui/card';
import { purchasingAPI, ASNMessage } from '../../lib/api';
import Changelog, { ChangelogEntry } from '../common/Changelog';
import { getApiErrorMessage } from '../../utils/errorUtils';
import { useFormatters } from '../../contexts/PreferencesContext';
import { useAuth } from '../../contexts/AuthContext';
import { useMenuSettings } from '../../contexts/MenuSettingsContext';
import { ASNDetailModalProps, ASNScheduleDetail, ASNDeliveryLineDetail } from './asnModalTypes';
import { asnDisplayStatus } from './asnDeliveryType';
import { ASNLineDocuments } from './ASNLineDocuments';
import { ASNDeliveryLinesGrid } from './ASNDeliveryLinesGrid';
import { ASNColumnPicker } from './ASNColumnPicker';
import { ASNDetailFooter } from './ASNDetailFooter';
import { ASNTrackingPanel } from './ASNTrackingPanel';
import { ASNDocumentsTab } from './ASNDocumentsTab';


export function ASNDetailModal({ isOpen, onClose, asn, onSubmit }: ASNDetailModalProps) {
  // CR-4888 round 2: gate ASN workflow buttons by role.
  // Supplier owns 'submit' (Created → Submitted). Customer owns 'acknowledge'
  // (Submitted → In Transit). isSupplierUser hides customer-only actions from
  // suppliers.
  // CR-4888 rework (Error 10b): the 'Mark as Delivered' action has been removed
  // from the footer for all roles — delivery is set implicitly when the customer
  // books a GRN. The button and its isCustomer gate are commented out below.
  const { user } = useAuth();
  // PNA-only Produmax CSV export — gated by the asn_produmax_export feature flag.
  const { isFeatureEnabled } = useMenuSettings();
  const isSupplierUser = user?.platform === 'SUPPLIER';
  // CR-6322 / CR-7194: the in-ASN column picker is editable by a customer OR a
  // supplier admin (mirrors the backend IsHelixOrCompanyAdmin gate, which
  // admits both). Each saves their own company's layout — a supplier's choice
  // drives its grid and CSV, never the customer's printed note. Helix operators
  // and superusers configure on a company's behalf via the site switcher.
  // Logistics is deliberately excluded: a driver configures nothing, and price
  // is stripped from their payload server-side regardless.
  const canEditColumns = !!(
    (user?.is_company_admin && (
      user?.company_type === 'customer' || user?.platform === 'CUSTOMER'
      || user?.company_type === 'supplier' || user?.platform === 'SUPPLIER'
    ))
    || user?.company_type === 'helix'
    || user?.is_superuser
  );
  // CR-4888 rework (Error 10b): only referenced by the commented-out
  // 'Mark as Delivered' button below; kept (commented) for easy restoration.
  // const isCustomer = user?.company_type === 'customer' || user?.platform === 'CUSTOMER';
  const [asnData, setAsnData] = useState(asn);
  const [loading, setLoading] = useState(false);
  // CR-5762: the delivery line whose per-line attachments dialog is open.
  const [lineDocsFor, setLineDocsFor] = useState<ASNDeliveryLineDetail | null>(null);
  // CR-6322: per-customer delivery-note column picker (customer admins only).
  const [showColumnPicker, setShowColumnPicker] = useState(false);

  // CR-6160: paid ASN logistics-tracking module. The feature flag gates all
  // tracking UI + the refresh action; when off, a premium hint is shown instead.
  const trackingEnabled = isFeatureEnabled('asn_logistics_tracking');
  const [refreshingTracking, setRefreshingTracking] = useState(false);

  const handleRefreshTracking = async () => {
    setRefreshingTracking(true);
    try {
      const res = await purchasingAPI.refreshASNTracking(asnData.id);
      setAsnData(res.data.data);
      // A 'skipped' response (courier/number not trackable, or the provider was
      // unavailable) is a no-op, not a success — surface it as info so the UI
      // feedback matches the API instead of implying the refresh worked.
      if (res.data.status === 'skipped') {
        toast.info(res.data.message || 'No tracking update available right now.');
      } else {
        toast.success(res.data.message || 'Tracking updated');
      }
    } catch (err) {
      toast.error(getApiErrorMessage(err, 'Failed to refresh tracking'));
    } finally {
      setRefreshingTracking(false);
    }
  };

  // CR-3417: Document count for the tab badge (documents tab owns its own state).
  const [documentCount, setDocumentCount] = useState(0);

  // CR-3859: Activity log state
  const [activityLog, setActivityLog] = useState<ChangelogEntry[]>([]);
  const [loadingActivityLog, setLoadingActivityLog] = useState(false);

  // CR-3860: Communication state
  const [newNote, setNewNote] = useState('');
  const [submittingNote, setSubmittingNote] = useState(false);
  const [messages, setMessages] = useState<ASNMessage[]>([]);
  const [loadingMessages, setLoadingMessages] = useState(false);

  useEffect(() => {
    if (isOpen && asn.id) {
      fetchASNDetails();
      fetchActivityLog();
    }
  }, [isOpen, asn.id]);

  const fetchASNDetails = async () => {
    try {
      const response = await purchasingAPI.getASN(asn.id);
      setAsnData(response.data);
    } catch (error) {
      console.error('Failed to fetch ASN details:', error);
    }
  };

  const fetchActivityLog = async () => {
    if (!asn.id) return;
    try {
      setLoadingActivityLog(true);
      const response = await purchasingAPI.getChangelogs({ asn: asn.id, ordering: '-change_date', page_size: 50 });
      const transformedLogs: ChangelogEntry[] = (response.data.results || []).map((log) => ({
        id: log.id,
        change_date: log.change_date,
        changed_by: log.changed_by_details?.full_name || 'System',
        field_changed: log.field_changed,
        old_value: log.old_value,
        new_value: log.new_value,
        reason: log.reason || '',
        change_type: log.field_changed || '',
      }));
      setActivityLog(transformedLogs);
    } catch (error) {
      console.error('Failed to fetch ASN activity log:', error);
    } finally {
      setLoadingActivityLog(false);
    }
  };

  const fetchMessages = async () => {
    if (!asn.id) return;
    try {
      setLoadingMessages(true);
      const res = await purchasingAPI.getASNMessages({ asn: asn.id, page_size: 100 });
      setMessages(res.data.results || []);
    } catch (error) {
      console.error('Failed to fetch ASN messages:', error);
    } finally {
      setLoadingMessages(false);
    }
  };

  const handleAddNote = async () => {
    if (!newNote.trim() || !asn.id) return;
    try {
      setSubmittingNote(true);
      await purchasingAPI.createASNMessage({ asn: asn.id, message_text: newNote.trim() });
      setNewNote('');
      await fetchMessages();
    } catch (error) {
      console.error('Failed to send message:', error);
      alert('Failed to send message');
    } finally {
      setSubmittingNote(false);
    }
  };

  const [printing, setPrinting] = useState(false);

  const handlePrintASN = async () => {
    setPrinting(true);
    try {
      const response = await purchasingAPI.printASN(asnData.id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 30000);
    } catch (error: unknown) {
      console.error('Failed to print ASN:', error);
    } finally {
      setPrinting(false);
    }
  };

  const handleWorkflowAction = async (action: string, data?: { actual_delivery_date?: string }) => {
    setLoading(true);
    try {
      if (action === 'submit') await purchasingAPI.submitASN(asnData.id);
      else if (action === 'acknowledge') await purchasingAPI.acknowledgeASN(asnData.id);
      else if (action === 'mark_delivered') await purchasingAPI.markASNDelivered(asnData.id, data || {});
      await fetchASNDetails();
      await onSubmit({});
    } catch (error: unknown) {
      console.error('Workflow action failed:', error);
      alert(getApiErrorMessage(error, 'Failed to process workflow action'));
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status: string) => {
    const statusMap: Record<string, { label: string; class: string }> = {
      created: { label: 'Created', class: 'bg-gray-100 text-gray-800' },
      submitted: { label: 'Submitted', class: 'bg-blue-100 text-blue-800' },
      // CR-7142: a direct load that has been acknowledged. It is stored as
      // 'submitted' because it must never enter In Transit, so without this the
      // badge would keep saying "Submitted" after the customer acknowledged it.
      acknowledged: { label: 'Acknowledged', class: 'bg-purple-100 text-purple-800' },
      in_transit: { label: 'In Transit', class: 'bg-yellow-100 text-yellow-800' },
      delivered: { label: 'Delivered', class: 'bg-green-100 text-green-800' },
      received: { label: 'Received', class: 'bg-green-100 text-green-800' },
      cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
    };
    const config = statusMap[status] || { label: status, class: 'bg-gray-100 text-gray-800' };
    return <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
  };

  const { formatDate: contextFormatDate, formatDateTime } = useFormatters();
  const formatDate = (dateString?: string) => {
    if (!dateString) return '-';
    return contextFormatDate(dateString);
  };

  const [detailTab, setDetailTab] = useState<'lines' | 'documents' | 'communication' | 'activity'>('lines');

  // Fetch messages when communication tab is opened
  useEffect(() => {
    if (detailTab === 'communication' && asn.id) {
      fetchMessages();
    }
  }, [detailTab, asn.id]);

  if (!isOpen) return null;

  const scheduleCount = asnData.schedules_details?.length || 0;
  const deliveryLineCount = asnData.delivery_lines_details?.length || 0;
  const lineCount = scheduleCount + deliveryLineCount;

  return (
    <>
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
        <Card className="w-full max-w-5xl max-h-[90vh] flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between p-4 border-b">
            <div className="flex items-center gap-3">
              <Truck className="h-5 w-5 text-blue-600" />
              <h3 className="text-lg font-semibold">ASN Details</h3>
              {getStatusBadge(asnDisplayStatus(asnData))}
            </div>
            <button onClick={onClose} className="p-1 hover:bg-muted rounded-full transition-colors">
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Compact Summary Grid */}
          <div className="px-6 py-4 border-b bg-muted/20">
            <div className="grid grid-cols-4 gap-x-8 gap-y-3 text-sm">
              <div><span className="text-muted-foreground">ASN Number</span><p className="font-medium">{asnData.asn_number}</p></div>
              <div><span className="text-muted-foreground">Supplier</span><p className="font-medium">{asnData.supplier_details?.name || '-'}</p></div>
              <div><span className="text-muted-foreground">Customer</span><p className="font-medium">{asnData.customer_details?.name || '-'}</p></div>
              <div><span className="text-muted-foreground">Courier</span><p className="font-medium">{asnData.courier?.toUpperCase() || '-'}</p></div>
              <div><span className="text-muted-foreground">Tracking Number</span><p className="font-medium font-mono">{asnData.tracking_number || '-'}</p></div>
              <div><span className="text-muted-foreground">Invoice Number</span><p className="font-medium font-mono">{asnData.invoice_number || '-'}</p></div>
              <div><span className="text-muted-foreground">Transport</span><p className="font-medium capitalize">{asnData.method_of_transport || '-'}</p></div>
              <div><span className="text-muted-foreground">Expected Delivery</span><p className="font-medium">{formatDate(asnData.expected_delivery_date)}</p></div>
              <div><span className="text-muted-foreground">Actual Delivery</span><p className="font-medium">{formatDate(asnData.actual_delivery_date)}</p></div>
              <div><span className="text-muted-foreground">Weight (kg)</span><p className="font-medium">{asnData.computed_weight_kg ?? asnData.weight_kg ?? '-'}</p></div>
              <div><span className="text-muted-foreground">Distance (km)</span><p className="font-medium">{asnData.distance_km || '-'}</p></div>
              <div><span className="text-muted-foreground">Carbon (kg CO2)</span><p className="font-medium text-green-700">{asnData.carbon_emission_kg || asnData.calculated_carbon_emission || '-'}</p></div>
              {asnData.notes && <div><span className="text-muted-foreground">Notes</span><p className="font-medium truncate" title={asnData.notes}>{asnData.notes}</p></div>}
            </div>
          </div>

          {/* CR-6160: Live Courier Tracking (paid module). Full UI when the
              customer is subscribed; a premium hint when not. */}
          <ASNTrackingPanel
            asnData={asnData}
            trackingEnabled={trackingEnabled}
            refreshingTracking={refreshingTracking}
            onRefresh={handleRefreshTracking}
            formatDate={formatDate}
          />

          {/* Tabs */}
          <div className="border-b px-6">
            <div className="flex gap-1">
              {([
                { key: 'lines' as const, label: `Lines (${lineCount})`, icon: Package },
                { key: 'documents' as const, label: `Documents (${documentCount})`, icon: FolderOpen },
                { key: 'communication' as const, label: 'Communication', icon: MessageSquare },
                { key: 'activity' as const, label: 'Activity Log', icon: Clock },
              ]).map(({ key, label, icon: Icon }) => (
                <button key={key} onClick={() => setDetailTab(key)}
                  className={`flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition-colors ${detailTab === key ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'}`}>
                  <Icon className="h-4 w-4" />
                  {label}
                </button>
              ))}
            </div>
          </div>

          {/* Tab Content */}
          <div className="flex-1 overflow-y-auto p-6">
            {/* Lines Tab */}
            {detailTab === 'lines' && (
              <>
                {asnData.delivery_lines_details && asnData.delivery_lines_details.length > 0 ? (
                  <ASNDeliveryLinesGrid
                    lines={asnData.delivery_lines_details}
                    columnFormat={asnData.column_format}
                    formatDate={formatDate}
                    onOpenAttachments={setLineDocsFor}
                    canEditColumns={canEditColumns}
                    onEditColumns={() => setShowColumnPicker(true)}
                  />
                ) : asnData.schedules_details && asnData.schedules_details.length > 0 ? (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b bg-muted/30">
                          <th className="text-left py-2 px-3 font-medium">PO / Line</th>
                          <th className="text-left py-2 px-3 font-medium">Part Number</th>
                          <th className="text-left py-2 px-3 font-medium">Description</th>
                          <th className="text-right py-2 px-3 font-medium">Schedule Qty</th>
                          <th className="text-right py-2 px-3 font-medium">Shipped Qty</th>
                          <th className="text-right py-2 px-3 font-medium">Balance</th>
                          <th className="text-center py-2 px-3 font-medium">Status</th>
                          <th className="text-left py-2 px-3 font-medium">Due Date</th>
                          <th className="text-left py-2 px-3 font-medium">GRN</th>
                        </tr>
                      </thead>
                      <tbody>
                        {asnData.schedules_details.map((schedule: ASNScheduleDetail, idx: number) => {
                          const getScheduleStatusBadge = (status: string) => {
                            const statusMap: Record<string, { label: string; class: string }> = {
                              scheduled: { label: 'Scheduled', class: 'bg-blue-100 text-blue-800' },
                              partially_shipped: { label: 'Partial', class: 'bg-yellow-100 text-yellow-800' },
                              shipped: { label: 'Shipped', class: 'bg-green-100 text-green-800' },
                              cancelled: { label: 'Cancelled', class: 'bg-red-100 text-red-800' },
                            };
                            const config = statusMap[status] || { label: status || '-', class: 'bg-gray-100 text-gray-800' };
                            return <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${config.class}`}>{config.label}</span>;
                          };

                          return (
                            <tr key={idx} className="border-b hover:bg-muted/30">
                              <td className="py-2 px-3">
                                {/* CR-1154: back-link to the PO line item detail. */}
                                <div className="font-medium">
                                  <PoHeaderLink poNumber={schedule.po_number || schedule.po_line_details?.po_details?.po_number || undefined}>
                                    {schedule.po_number || schedule.po_line_details?.po_details?.po_number || '-'}
                                  </PoHeaderLink>
                                </div>
                                {/* CR-1154: both PO number and line deep-link to the line item. */}
                                <div className="text-xs text-muted-foreground">
                                  <PoLineLink poLineId={schedule.po_line_id}>
                                    Line {schedule.line_number || schedule.po_line_details?.line_number}
                                  </PoLineLink>
                                </div>
                              </td>
                              <td className="py-2 px-3 font-mono text-xs">{schedule.part_number || schedule.po_line_details?.part_number || '-'}</td>
                              <td className="py-2 px-3 text-xs max-w-[200px] truncate" title={schedule.description || schedule.po_line_details?.description}>{schedule.description || schedule.po_line_details?.description || '-'}</td>
                              <td className="py-2 px-3 text-right">{schedule.schedule_qty || schedule.quantity || '-'}<span className="text-xs text-muted-foreground ml-1">{schedule.unit_of_measure || schedule.po_line_details?.unit_of_measure || ''}</span></td>
                              <td className="py-2 px-3 text-right font-medium text-green-700">{schedule.shipped_qty ?? '-'}</td>
                              <td className="py-2 px-3 text-right"><span className={parseFloat(schedule.balance_qty || '0') > 0 ? 'text-orange-600 font-medium' : ''}>{schedule.balance_qty ?? '-'}</span></td>
                              <td className="py-2 px-3 text-center">{getScheduleStatusBadge(schedule.status || '')}</td>
                              <td className="py-2 px-3">{formatDate(schedule.schedule_date || schedule.due_date)}</td>
                              <td className="py-2 px-3">
                                {schedule.grn_number ? (
                                  <span className="px-2 py-0.5 rounded bg-green-100 text-green-800 text-xs font-medium">{schedule.grn_number}</span>
                                ) : schedule.status === 'shipped' ? (
                                  <span className="text-xs text-muted-foreground italic">Awaiting GRN</span>
                                ) : (
                                  <span className="text-muted-foreground">-</span>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No line details available</div>
                )}
              </>
            )}

            {/* Documents Tab — always mounted (hidden when inactive) so the
                count badge stays live without an extra fetch in the parent. */}
            <div className={detailTab === 'documents' ? '' : 'hidden'}>
              <ASNDocumentsTab asnId={asn.id} onCountChange={setDocumentCount} />
            </div>

            {/* Communication Tab */}
            {detailTab === 'communication' && (
              <div className="space-y-4">
                <div className="bg-muted/30 rounded-lg p-4">
                  <label className="block text-sm font-medium text-muted-foreground mb-2">Add a note or message</label>
                  <div className="flex gap-2">
                    <textarea value={newNote} onChange={(e) => setNewNote(e.target.value)} placeholder="Enter your message..." rows={3} className="flex-1 px-3 py-2 text-sm border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary resize-none" />
                    <button onClick={handleAddNote} disabled={submittingNote || !newNote.trim()} className="px-4 py-2 text-sm font-medium bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors disabled:opacity-50 self-end">
                      {submittingNote ? 'Sending...' : 'Send'}
                    </button>
                  </div>
                </div>
                {loadingMessages ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Loading messages...</div>
                ) : messages.length > 0 ? (
                  <div className="space-y-3 max-h-[400px] overflow-auto">
                    {messages.map((msg) => (
                      <div key={msg.id} className="border rounded-lg p-3 bg-white">
                        <div className="flex justify-between items-center mb-1">
                          <span className="text-sm font-medium">{msg.sender_details?.full_name || 'Unknown'}</span>
                          <span className="text-xs text-muted-foreground">{formatDateTime(msg.sent_date)}</span>
                        </div>
                        <p className="text-sm text-muted-foreground">{msg.message_text}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No messages yet</div>
                )}
              </div>
            )}

            {/* Activity Log Tab */}
            {detailTab === 'activity' && (
              <>
                {loadingActivityLog ? (
                  <div className="text-sm text-muted-foreground py-8 text-center">Loading activity log...</div>
                ) : activityLog.length === 0 ? (
                  <div className="text-sm text-muted-foreground py-8 text-center border rounded-lg bg-muted/10">No activity recorded yet</div>
                ) : (
                  <Changelog entries={activityLog} maxHeight="400px" emptyMessage="No activity recorded" />
                )}
              </>
            )}
          </div>

          <ASNDetailFooter
            asnData={asnData}
            loading={loading}
            printing={printing}
            isSupplierUser={isSupplierUser}
            produmaxExportEnabled={isFeatureEnabled('asn_produmax_export')}
            onWorkflowAction={handleWorkflowAction}
            onPrint={handlePrintASN}
            onClose={onClose}
          />
        </Card>
      </div>
      {lineDocsFor && (
        <ASNLineDocuments
          isOpen={!!lineDocsFor}
          onClose={() => setLineDocsFor(null)}
          asnId={asnData.id}
          deliveryLineId={lineDocsFor.id}
          lineLabel={lineDocsFor.delivery_number || lineDocsFor.part_number || `Line ${lineDocsFor.id}`}
        />
      )}
      {canEditColumns && (
        <ASNColumnPicker
          isOpen={showColumnPicker}
          onClose={() => setShowColumnPicker(false)}
          onSaved={fetchASNDetails}
        />
      )}
    </>
  );
}
